/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// One-shot bond perception: parses the SDF through Miew's SDFParser with
// autoBond enabled (which uses lib/chem/AutoBond.js to fill in missing
// covalent bonds based on interatomic distances), then re-emits a fresh
// V2000 mol block so the new bond table travels with molecule.data and
// future renders no longer need a runtime autoBond flag.

import SDFParser from './lib/io/parsers/SDFParser';
import { Util } from './Util.ts';

const titleCaseSymbol = (s: string): string => {
  const trimmed = s.trim();
  if (!trimmed) return '';
  if (trimmed.length === 1) return trimmed.toUpperCase();
  return trimmed.charAt(0).toUpperCase() + trimmed.substring(1).toLowerCase();
};

const f10 = (n: number): string => n.toFixed(4).padStart(10, ' ');
const i3 = (n: number): string => String(n).padStart(3, ' ');

/**
 * Emit a V2000 mol block from a parsed Miew Complex. Format-agnostic — works
 * on the output of SDFParser, PDBParser, XYZParser, etc. Returns null if the
 * complex is empty or has no atoms.
 */
export const complexToSdf = (complex: any, comment = '     AIMS'): string | null => {
  if (!complex) return null;
  const atoms: any[] = complex._atoms ?? [];
  const bonds: any[] = complex._bonds ?? [];
  if (atoms.length === 0) return null;

  const atomIndex = new Map<any, number>();
  atoms.forEach((a, i) => atomIndex.set(a, i + 1));

  const lines: string[] = [];
  // V2000 header is 3 lines (title, program, comment) before the counts
  // line. The title MUST be non-empty here: Util.correctSdf unconditionally
  // rewrites the first two characters of the emitted SDF (a bug in its
  // bondCountPosition handling that only manifests when atomNumber <= 999).
  // With a non-empty title that overwrite damages only the title — which
  // SDFParser treats as freeform text — instead of eating into the
  // program-line newline and collapsing the header from 4 lines to 3.
  lines.push('Molecule');
  lines.push(comment);
  lines.push('');
  lines.push(`${i3(atoms.length)}${i3(bonds.length)}  0  0  0  0  0  0  0  0999 V2000`);

  for (const a of atoms) {
    const p = a.position;
    const sym = titleCaseSymbol(a.element?.name ?? '').padEnd(3, ' ');
    lines.push(`${f10(p.x)}${f10(p.y)}${f10(p.z)} ${sym} 0  0  0  0  0  0  0  0  0  0  0  0`);
  }

  for (const b of bonds) {
    const i1 = atomIndex.get(b._left) ?? 0;
    const i2 = atomIndex.get(b._right) ?? 0;
    if (!i1 || !i2) continue;
    const order = b._order && b._order > 0 && b._order <= 4 ? b._order : 1;
    const [a, c] = i1 < i2 ? [i1, i2] : [i2, i1];
    lines.push(`${i3(a)}${i3(c)}${i3(order)}  0  0  0  0`);
  }

  lines.push('M  END');
  return lines.join('\n') + '\n';
};

export const autoBondSdf = async (sdf: string): Promise<string | null> => {
  try {
    const parser = new SDFParser(Util.correctSdf(sdf), { autoBond: true });
    const complex: any = await parser.parse();
    return complexToSdf(complex, '     AIMS auto-bond');
  } catch (err) {
    console.error('autoBondSdf failed:', err);
    return null;
  }
};
